import React from "react";
import { motion } from "framer-motion";
import Marquee from "react-fast-marquee";
import { Link } from "react-router-dom";
import { useSeo } from "@/lib/useSeo";
import {
  Reveal, LineReveal, Counter, Overline, GlowOrb, GridBackdrop,
  SectionHeading, PillButton,
} from "@/components/primitives";
import { Icon } from "@/lib/icons";
import {
  HERO, STATS, WHAT_WE_DO_CARDS, INFRA_NODES, GROWTH_POINTS, GROWTH_STATEMENT,
  CAPITAL_TREE, CAPTAL_TREE_DISCLAIMER, IMAGES,
} from "@/lib/content";
import { Magnetic, ParticleField } from "@/components/effects";
import { WhyVardhix, Lifecycle, Architecture, Security, Industries } from "@/components/sections/HomeV2";

/* ---------------- Hero ---------------- */
function Hero() {
  return (
    <section className="relative min-h-screen flex items-center overflow-hidden pt-28 pb-20" data-testid="hero-section">
      {/* Background image blend */}
      <div className="absolute inset-0">
        <img src={IMAGES.heroBg} alt="" className="h-full w-full object-cover opacity-[0.18]" />
        <div className="absolute inset-0 bg-gradient-to-b from-navy/60 via-navy/85 to-navy" />
      </div>
      {/* Animated particle network */}
      <div className="absolute inset-0 opacity-70">
        <ParticleField />
      </div>
      <GridBackdrop />
      <GlowOrb className="h-[520px] w-[520px] top-10 -left-32" />
      <GlowOrb className="h-[420px] w-[420px] bottom-0 right-0" color="rgba(26,232,189,0.12)" />

      <div className="relative mx-auto max-w-7xl px-5 sm:px-8 w-full grid lg:grid-cols-12 gap-12 items-center">
        {/* Left: copy */}
        <div className="lg:col-span-7">
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }}>
            <Overline>{HERO.overline}</Overline>
          </motion.div>

          <h1 className="mt-6 text-4xl sm:text-6xl lg:text-7xl font-heading font-bold tracking-tighter leading-[1.02] text-white">
            <LineReveal lines={HERO.headlineLines} lineClass="" />
          </h1>

          <Reveal delay={0.7}>
            <p className="mt-8 text-base md:text-lg text-slate-300 leading-relaxed max-w-2xl">
              {HERO.sub}
            </p>
          </Reveal>

          <Reveal delay={0.8}>
            <div className="mt-10 flex flex-wrap gap-4">
              <Magnetic>
                <PillButton to={HERO.primaryCta.to} testid="hero-primary-cta">{HERO.primaryCta.label}</PillButton>
              </Magnetic>
              <Magnetic>
                <PillButton to={HERO.secondaryCta.to} variant="secondary" testid="hero-secondary-cta" icon="ArrowUpRight">
                  {HERO.secondaryCta.label}
                </PillButton>
              </Magnetic>
            </div>
          </Reveal>

          <Reveal delay={0.95}>
            <div className="mt-14 flex items-center gap-3 text-xs uppercase tracking-[0.25em] text-slate-400">
              <span className="h-px w-10 bg-teal/50" />
              {HERO.trustLine}
            </div>
          </Reveal>
        </div>

        {/* Right: animated technology visualization placeholder */}
        <div className="lg:col-span-5 hidden lg:block">
          <HeroVisual />
        </div>
      </div>
    </section>
  );
}

/* Animated technology visualization / video placeholder */
function HeroVisual() {
  const nodes = ["Data Systems", "Trading Infra", "Automation", "Analytics", "Cloud Services"];
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.96, y: 20 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      transition={{ delay: 0.6, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
      className="relative glass rounded-3xl p-6 overflow-hidden"
      data-testid="hero-visual"
    >
      <div className="pointer-events-none absolute -top-16 -right-16 h-48 w-48 rounded-full bg-teal/15 blur-3xl" />
      {/* Faux media / play placeholder */}
      <div className="relative rounded-2xl overflow-hidden border border-white/10 aspect-video bg-matte">
        <img src={IMAGES.infra} alt="Vardhix technology visualization" className="absolute inset-0 h-full w-full object-cover opacity-40" />
        <div className="absolute inset-0 bg-gradient-to-t from-matte via-transparent to-transparent" />
        <button
          type="button"
          aria-label="Play introduction"
          data-testid="hero-play"
          className="group absolute inset-0 flex items-center justify-center"
        >
          <span className="relative flex h-16 w-16 items-center justify-center rounded-full bg-teal text-navy shadow-[0_0_30px_rgba(0,212,170,0.5)] transition-transform duration-300 group-hover:scale-110">
            <span className="absolute inset-0 rounded-full bg-teal/50 animate-ping" />
            <svg viewBox="0 0 24 24" className="relative h-6 w-6 translate-x-0.5" fill="currentColor"><path d="M8 5v14l11-7z" /></svg>
          </span>
        </button>
        <span className="absolute bottom-3 left-3 text-[11px] uppercase tracking-[0.2em] text-slate-300">Technology Overview</span>
      </div>
      {/* Live node ticker */}
      <div className="mt-5 space-y-2">
        {nodes.map((n, i) => (
          <motion.div
            key={n}
            initial={{ opacity: 0, x: 12 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.9 + i * 0.1 }}
            className="flex items-center justify-between rounded-xl border border-white/10 bg-white/5 px-4 py-2.5"
          >
            <span className="flex items-center gap-2.5 text-xs text-slate-200">
              <span className="h-1.5 w-1.5 rounded-full bg-teal animate-pulse-glow" />{n}
            </span>
            <span className="text-[10px] uppercase tracking-wider text-teal">Active</span>
          </motion.div>
        ))}
      </div>
    </motion.div>
  );
}

/* ---------------- Marquee ---------------- */
function Ribbon() {
  const items = [
    "TECHNOLOGY INFRASTRUCTURE", "BUSINESS AUTOMATION", "SCALABLE PLATFORMS",
    "FINTECH SOFTWARE", "TRADING EVALUATION", "RISK SYSTEMS", "DATA ANALYTICS",
  ];
  return (
    <div className="border-y border-white/10 bg-matte/60 py-5" data-testid="hero-marquee">
      <Marquee speed={40} gradient gradientColor="#0B0F19" gradientWidth={120}>
        {items.concat(items).map((t, i) => (
          <span key={i} className="mx-8 inline-flex items-center gap-3 text-sm font-heading font-medium tracking-[0.2em] text-slate-400">
            {t}<span className="text-teal">&#10022;</span>
          </span>
        ))}
      </Marquee>
    </div>
  );
}

/* ---------------- Stats ---------------- */
function Stats() {
  return (
    <section className="relative py-24" data-testid="stats-section">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-px rounded-2xl overflow-hidden border border-white/10 bg-white/5">
          {STATS.map((s, i) => (
            <Reveal key={i} delay={i * 0.06} className="bg-matte p-8">
              <div className="text-3xl md:text-4xl font-heading font-bold text-gradient-teal">
                <Counter value={s.value} prefix={s.prefix} suffix={s.suffix} decimals={Number.isInteger(s.value) ? 0 : 1} />
              </div>
              <p className="mt-3 text-sm text-slate-300 leading-snug">{s.label}</p>
              {s.note && <p className="mt-3 text-[11px] text-slate-500 leading-snug">{s.note}</p>}
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------------- Intro ---------------- */
function Intro() {
  const paras = [
    "Vardhix Technologies Private Limited is an Indian technology company focused on creating high-performance software platforms, fintech infrastructure, business automation tools, and digital ecosystems.",
    "We manage the technology, operations, product development, infrastructure, risk systems, support systems, and strategic growth of our associated platforms.",
    "Our objective is to create reliable and scalable technology solutions that solve real operational challenges for businesses and users across India.",
  ];
  return (
    <section className="relative py-24 border-t border-white/10">
      <div className="mx-auto max-w-7xl px-5 sm:px-8 grid lg:grid-cols-12 gap-12 items-start">
        <div className="lg:col-span-5">
          <SectionHeading overline="01 — Who We Are" title="A Technology Company Built for Scale" />
        </div>
        <div className="lg:col-span-7 space-y-6">
          {paras.map((p, i) => (
            <Reveal key={i} delay={i * 0.08}>
              <p className="text-lg text-slate-300 leading-relaxed">{p}</p>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------------- What We Do ---------------- */
function WhatWeDo() {
  return (
    <section className="relative py-24 border-t border-white/10" data-testid="what-we-do-section">
      <GlowOrb className="h-80 w-80 top-0 right-1/4" color="rgba(0,212,170,0.08)" />
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <SectionHeading overline="02 — What We Do" title="Technology across the fintech stack" sub="Six core capabilities powering our platforms and the businesses we build for." />
        <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {WHAT_WE_DO_CARDS.map((c, i) => (
            <Reveal key={c.title} delay={(i % 3) * 0.08}>
              <motion.div
                whileHover={{ y: -6 }}
                transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
                className="group h-full glass rounded-2xl p-8 hover:border-teal/40 relative overflow-hidden"
                data-testid={`service-card-${i}`}
              >
                <div className="pointer-events-none absolute -bottom-16 -right-16 h-40 w-40 rounded-full bg-teal/10 blur-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                <span className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-teal/10 border border-teal/30 text-teal">
                  <Icon name={c.icon} className="h-6 w-6" />
                </span>
                <h3 className="mt-6 text-xl font-heading font-semibold text-white">{c.title}</h3>
                <p className="mt-3 text-sm text-slate-400 leading-relaxed">{c.desc}</p>
                <Link to="/what-we-do" className="mt-6 inline-flex items-center gap-1.5 text-sm font-medium text-teal hover:text-teal-glow transition-colors">
                  Learn More <Icon name="ArrowRight" className="h-3.5 w-3.5" />
                </Link>
              </motion.div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------------- Featured Platform ---------------- */
function Featured() {
  return (
    <section className="relative py-24 border-t border-white/10">
      <div className="mx-auto max-w-7xl px-5 sm:px-8 grid lg:grid-cols-2 gap-14 items-center">
        <Reveal>
          <div className="relative rounded-3xl overflow-hidden border border-white/10">
            <img src={IMAGES.trading} alt="Capital Tree Funding trading evaluation dashboard with candlestick charts" className="h-full w-full object-cover aspect-[4/3]" />
            <div className="absolute inset-0 bg-gradient-to-tr from-navy/70 to-transparent" />
            <div className="absolute bottom-5 left-5 glass rounded-full px-4 py-2 text-xs text-teal">
              &#9679; Live Platform
            </div>
          </div>
        </Reveal>
        <div>
          <SectionHeading overline="03 — Featured Platform" title={CAPITAL_TREE.name} />
          <Reveal delay={0.1}>
            <p className="mt-6 text-slate-300 leading-relaxed">
              Capital Tree Funding is a technology-enabled trading evaluation platform designed for
              Indian market participants. The platform provides simulated trading environments,
              performance evaluation systems, risk management tools, analytics, account monitoring,
              and trader-focused technology infrastructure.
            </p>
          </Reveal>
          <Reveal delay={0.16}>
            <div className="mt-8 flex flex-wrap gap-4">
              <PillButton href={CAPITAL_TREE.url} testid="featured-visit-cta" icon="ExternalLink">Visit Capital Tree Funding</PillButton>
              <PillButton to="/platforms" variant="secondary" testid="featured-explore-cta">Explore Platform Technology</PillButton>
            </div>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-6 text-xs text-slate-500 leading-relaxed border-l-2 border-teal/40 pl-4">
              {CAPTAL_TREE_DISCLAIMER}
            </p>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

/* ---------------- Infrastructure ---------------- */
function Infrastructure() {
  return (
    <section className="relative py-24 border-t border-white/10 overflow-hidden" data-testid="infra-section">
      <div className="absolute inset-0">
        <img src={IMAGES.infra} alt="" className="h-full w-full object-cover opacity-[0.12]" />
        <div className="absolute inset-0 bg-navy/80" />
      </div>
      <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
        <SectionHeading center overline="04 — Infrastructure" title="Technology Infrastructure Designed for Performance" sub="Our technology systems are designed to process large volumes of user activity, enforce platform rules, monitor risk, generate insights, and deliver a reliable digital experience at scale." />
        <div className="mt-14 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {INFRA_NODES.map((node, i) => (
            <Reveal key={node} delay={(i % 4) * 0.05}>
              <motion.div
                whileHover={{ y: -4 }}
                className="glass rounded-xl p-5 h-full flex items-start gap-3 hover:border-teal/40"
              >
                <span className="mt-0.5 h-2 w-2 shrink-0 rounded-full bg-teal shadow-[0_0_10px_#00D4AA] animate-pulse-glow" />
                <span className="text-sm text-slate-200 leading-snug">{node}</span>
              </motion.div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------------- Growth ---------------- */
function GrowthPreview() {
  return (
    <section className="relative py-24 border-t border-white/10">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <SectionHeading overline="05 — Growth" title="Built Fast. Built Responsibly. Built to Scale." />
        <div className="mt-12 grid md:grid-cols-2 gap-10 items-center">
          <div className="grid sm:grid-cols-2 gap-4">
            {GROWTH_POINTS.map((g, i) => (
              <Reveal key={g.label} delay={(i % 2) * 0.06}>
                <div className="flex items-center gap-3 glass rounded-xl px-4 py-4">
                  <span className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-teal/10 border border-teal/30 text-teal">
                    <Icon name={g.icon} className="h-4.5 w-4.5" />
                  </span>
                  <span className="text-sm text-slate-200">{g.label}</span>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.1}>
            <div className="glass rounded-2xl p-8 border-l-2 border-teal/50">
              <p className="text-lg text-slate-200 leading-relaxed">{GROWTH_STATEMENT}</p>
              <PillButton to="/growth" variant="ghost" testid="growth-view-cta" icon="ArrowRight">
                View Growth & Milestones
              </PillButton>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

/* ---------------- Vision & Mission ---------------- */
function VisionMission() {
  const cards = [
    { k: "Our Vision", v: "To build one of India’s most trusted technology ecosystems for fintech software, trading evaluation systems, digital infrastructure, automation, and scalable online platforms." },
    { k: "Our Mission", v: "To create practical, secure, transparent, and high-performance technology products that help businesses and users operate more efficiently in an increasingly digital economy." },
  ];
  return (
    <section className="relative py-24 border-t border-white/10">
      <div className="mx-auto max-w-7xl px-5 sm:px-8 grid md:grid-cols-2 gap-6">
        {cards.map((c, i) => (
          <Reveal key={c.k} delay={i * 0.1}>
            <div className="relative glass rounded-3xl p-10 h-full overflow-hidden">
              <GlowOrb className="h-40 w-40 -top-10 -right-10" color="rgba(0,212,170,0.12)" />
              <Overline>{c.k}</Overline>
              <p className="mt-6 text-xl md:text-2xl font-heading text-white leading-snug">{c.v}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

/* ---------------- CTA ---------------- */
function CTA() {
  return (
    <section className="relative py-28 border-t border-white/10 overflow-hidden">
      <GlowOrb className="h-96 w-96 top-0 left-1/3" />
      <div className="relative mx-auto max-w-4xl px-5 sm:px-8 text-center">
        <Reveal>
          <h2 className="text-4xl md:text-5xl font-heading font-semibold tracking-tight text-white">
            Let&rsquo;s Build the Future of Digital Infrastructure
          </h2>
        </Reveal>
        <Reveal delay={0.1}>
          <div className="mt-10 flex flex-wrap justify-center gap-4">
            <PillButton to="/contact" testid="cta-partner">Partner With Us</PillButton>
            <PillButton to="/contact" variant="secondary" testid="cta-contact">Contact Our Team</PillButton>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

export default function Home() {
  useSeo({
    description:
      "Vardhix Technologies Private Limited develops and operates fintech software, trading evaluation technologies, digital platforms, risk systems, automation solutions, and scalable technology products in India.",
    breadcrumb: [{ name: "Home", path: "/" }],
  });
  return (
    <>
      <Hero />
      <Ribbon />
      <Stats />
      <Intro />
      <WhyVardhix />
      <WhatWeDo />
      <Industries />
      <Featured />
      <Lifecycle />
      <Architecture />
      <Infrastructure />
      <Security />
      <GrowthPreview />
      <VisionMission />
      <CTA />
    </>
  );
}
